import { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  Form as AntForm,
  Grid,
  Input,
  Segmented,
  Typography,
} from 'antd';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Zap, Workflow } from 'lucide-react';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { authApi } from '@api/auth';
import { NormalizedApiError } from '@api/errors';
import { ErrorCode } from '@shared/constants/errorCodes';
import { useAuthStore } from '../store';
import { clearLastUser, readLastUser } from '../lastUser';
import { ROUTES } from '@shared/constants/routes';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import type { Translator } from '@/i18n/config';
import { recordAuditAction } from '@features/audit/recordAction';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

function initialsOf(fullName: string): string {
  return (
    fullName
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'HD'
  );
}

function makeLoginSchema(t: Translator) {
  return z.object({
    email: z
      .string()
      .min(1, t('validation:emailRequired'))
      .email(t('validation:email')),
    password: z.string().min(6, t('validation:passwordMin', { count: 6 })),
    remember: z.boolean().optional(),
  });
}

// K-20 — LDAP/AD girişinde alan kurumsal kullanıcı adı (sAMAccountName / UPN);
// e-posta biçimi ZORLANMAZ (ayrı şema, standart login union'ının LDAP dalı).
function makeLdapLoginSchema(t: Translator) {
  return z.object({
    username: z.string().min(1, t('validation:required')),
    password: z.string().min(1, t('validation:passwordRequired')),
  });
}

type LoginValues = z.infer<ReturnType<typeof makeLoginSchema>>;
type LdapLoginValues = z.infer<ReturnType<typeof makeLdapLoginSchema>>;
type LoginMode = 'standard' | 'ldap';

function BrandPanel() {
  const { t } = useTranslation(['auth', 'common', 'validation', 'errors']);
  return (
    <aside className="ds-login-brand" aria-hidden="true">
      <div className="ds-login-brand__inner">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="ds-login-brand__mark"><img src="/logo.svg" alt="" /></div>
          <div style={{ lineHeight: 1.15 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{t('brand.title')}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>{t('brand.subtitle')}</div>
          </div>
        </div>
      </div>

      <div className="ds-login-brand__inner" style={{ maxWidth: 420 }}>
        <h2 className="ds-login-brand__heading">
          {t('login.panel.heading')}
        </h2>
        <p className="ds-login-brand__lead">
          {t('login.panel.lead')}
        </p>
        <ul className="ds-login-brand__list">
          <li>
            <ShieldCheck size={18} strokeWidth={1.8} /> {t('login.panel.feature1')}
          </li>
          <li>
            <Zap size={18} strokeWidth={1.8} /> {t('login.panel.feature2')}
          </li>
          <li>
            <Workflow size={18} strokeWidth={1.8} /> {t('login.panel.feature3')}
          </li>
        </ul>
      </div>

      <div className="ds-login-brand__footer">
        <span>{t('brand.company', { year: new Date().getFullYear() })}</span>
        <span>{t('brand.version', { version: import.meta.env.VITE_APP_VERSION ?? 'dev' })}</span>
      </div>
    </aside>
  );
}

export function LoginPage() {
  const { t } = useTranslation(['auth', 'common', 'validation', 'errors']);
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const [serverError, setServerError] = useState<string | null>(null);
  const [lastUser, setLastUser] = useState(() => readLastUser());
  // 2FA challenge state is EPHEMERAL (single-use token, single screen) — kept in
  // local component state, NOT the persisted auth store, so it never survives a
  // reload.
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  // K-20 — giriş tipi dallanması. LDAP dalında alan = kullanıcı adı; 2FA adımı
  // ORTAK kalır (challengeToken ephemeral state, davranış değişmez).
  const [mode, setMode] = useState<LoginMode>('standard');
  const screens = useBreakpoint();
  const isWide = screens.lg;
  const loginSchema = useMemo(() => makeLoginSchema(t as unknown as Translator), [t]);
  const ldapLoginSchema = useMemo(() => makeLdapLoginSchema(t as unknown as Translator), [t]);

  const onAuthenticated = (data: Parameters<typeof setSession>[0]) => {
    setSession(data);
    toast.success(t('login.welcome', { name: data.user.fullName }));
    void recordAuditAction({
      action: 'auth.login',
      resource: 'auth',
      resourceId: data.user.id,
      summary: `${data.user.email} giriş yaptı`,
    });
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    void navigate(from ?? ROUTES.DASHBOARD, { replace: true });
  };

  // Shared login-error handler for BOTH standard and LDAP step-1.
  // AUTH_2FA_REQUIRED: step-1 succeeded but 2FA is on — pivot to the code
  // challenge instead of showing an error (identical for both providers).
  const handleLoginError = (err: unknown) => {
    if (
      err instanceof NormalizedApiError &&
      err.code === ErrorCode.AUTH_2FA_REQUIRED &&
      typeof err.data?.challengeToken === 'string'
    ) {
      setChallengeToken(err.data.challengeToken);
      setServerError(null);
      return;
    }
    setServerError(resolveApiErrorMessage(err));
  };

  const loginMutation = useMutation({
    mutationFn: (payload: LoginValues) =>
      authApi.login({ email: payload.email, password: payload.password }),
    onSuccess: onAuthenticated,
    onError: handleLoginError,
  });

  const ldapLoginMutation = useMutation({
    mutationFn: (payload: LdapLoginValues) =>
      authApi.loginLdap({ username: payload.username, password: payload.password }),
    onSuccess: onAuthenticated,
    onError: handleLoginError,
  });

  const changeMode = (next: LoginMode) => {
    setMode(next);
    setServerError(null);
  };

  const twoFactorMutation = useMutation({
    mutationFn: (code: string) => authApi.adminLoginTwoFactor(challengeToken ?? '', code),
    onSuccess: onAuthenticated,
    onError: (err) => setServerError(resolveApiErrorMessage(err)),
  });

  const cancelTwoFactor = () => {
    setChallengeToken(null);
    setTwoFactorCode('');
    setServerError(null);
  };

  const handleUseDifferentAccount = () => {
    clearLastUser();
    setLastUser(null);
    setServerError(null);
  };

  return (
    <div className="ds-login-shell">
      {isWide ? <BrandPanel /> : null}

      <section className="ds-login-form-section">
        <div className="ds-login-form">
          {!isWide ? (
            <div className="ds-auth-card__brand">
              <div className="ds-auth-card__brand-mark"><img src="/logo.svg" alt="" /></div>
              <div className="ds-auth-card__brand-text">
                <div className="ds-auth-card__brand-name">{t('brand.title')}</div>
                <div className="ds-auth-card__brand-sub">{t('brand.cardSubtitle')}</div>
              </div>
            </div>
          ) : null}

          {challengeToken ? (
            <>
              <Title level={3} className="ds-auth-card__title" style={{ marginBottom: 4 }}>
                {t('twoFactor.challengeTitle')}
              </Title>
              <Text className="ds-auth-card__subtitle">
                {t('twoFactor.challengeSubtitle')}
              </Text>
            </>
          ) : mode === 'ldap' ? (
            <>
              <Title level={3} className="ds-auth-card__title" style={{ marginBottom: 4 }}>
                {t('login.title')}
              </Title>
              <Text className="ds-auth-card__subtitle">
                {t('login.ldapSubtitle')}
              </Text>
            </>
          ) : lastUser ? (
            <div className="ds-login-returning">
              <Avatar
                size={64}
                src={resolveAssetUrl(lastUser.avatarUrl) ?? undefined}
                className="ds-login-returning__avatar"
                style={{
                  background: 'var(--ds-brand)',
                  color: 'var(--ds-text-on-brand)',
                  fontWeight: 600,
                  fontSize: 22,
                }}
              >
                {initialsOf(lastUser.fullName)}
              </Avatar>
              <Title level={3} className="ds-auth-card__title ds-login-returning__title">
                {t('login.returning.title', { name: lastUser.fullName })}
              </Title>
              <Text className="ds-auth-card__subtitle">
                {t('login.returning.subtitle')}
              </Text>
              <button
                type="button"
                className="ds-login-returning__switch"
                onClick={handleUseDifferentAccount}
              >
                {t('login.returning.differentAccount')}
              </button>
            </div>
          ) : (
            <>
              <Title level={3} className="ds-auth-card__title" style={{ marginBottom: 4 }}>
                {t('login.title')}
              </Title>
              <Text className="ds-auth-card__subtitle">
                {t('login.subtitle')}
              </Text>
            </>
          )}

          {!challengeToken ? (
            <Segmented<LoginMode>
              block
              value={mode}
              onChange={changeMode}
              options={[
                { label: t('login.mode.standard'), value: 'standard' },
                { label: t('login.mode.ldap'), value: 'ldap' },
              ]}
              style={{ marginBottom: 16 }}
              aria-label={t('login.mode.standard')}
            />
          ) : null}

          {serverError ? (
            <Alert
              type="error"
              message={serverError}
              showIcon
              closable
              onClose={() => setServerError(null)}
              style={{ marginBottom: 16 }}
            />
          ) : null}

          {challengeToken ? (
            <AntForm
              layout="vertical"
              component="div"
              onSubmitCapture={(e) => {
                e.preventDefault();
                const code = twoFactorCode.trim();
                if (code) twoFactorMutation.mutate(code);
              }}
            >
              <AntForm.Item label={t('twoFactor.codeLabel')} help={t('twoFactor.codeHint')}>
                <Input
                  size="large"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder={t('twoFactor.codePlaceholder')}
                  value={twoFactorCode}
                  maxLength={20}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  onPressEnter={() => {
                    const code = twoFactorCode.trim();
                    if (code) twoFactorMutation.mutate(code);
                  }}
                />
              </AntForm.Item>
              <Button
                type="primary"
                block
                size="large"
                loading={twoFactorMutation.isPending}
                disabled={twoFactorCode.trim().length === 0}
                onClick={() => {
                  const code = twoFactorCode.trim();
                  if (code) twoFactorMutation.mutate(code);
                }}
              >
                {t('twoFactor.verify')}
              </Button>
              <Button type="link" block onClick={cancelTwoFactor} style={{ marginTop: 8 }}>
                {t('twoFactor.back')}
              </Button>
            </AntForm>
          ) : mode === 'ldap' ? (
            <Form
              key="ldap"
              schema={ldapLoginSchema}
              defaultValues={{ username: '', password: '' }}
              onSubmit={(values) => ldapLoginMutation.mutate(values)}
            >
              <AntForm layout="vertical" component="div">
                <FormField name="username" label={t('login.fields.usernameLabel')} required>
                  <Input
                    autoComplete="username"
                    placeholder={t('login.fields.usernamePlaceholder')}
                    size="large"
                  />
                </FormField>
                <FormField name="password" label={t('login.fields.passwordLabel')} required>
                  <Input.Password
                    autoComplete="current-password"
                    placeholder={t('login.fields.passwordPlaceholder')}
                    size="large"
                  />
                </FormField>
              </AntForm>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={ldapLoginMutation.isPending}
              >
                {t('login.submit')}
              </Button>
            </Form>
          ) : (
            <Form
              key={lastUser?.email ?? 'fresh'}
              schema={loginSchema}
              defaultValues={{ email: lastUser?.email ?? '', password: '', remember: true }}
              onSubmit={(values) => loginMutation.mutate(values)}
            >
              <AntForm layout="vertical" component="div">
                <FormField name="email" label={t('login.fields.emailLabel')} required>
                  <Input
                    type="email"
                    autoComplete="username"
                    placeholder={t('login.fields.emailPlaceholder')}
                    size="large"
                    readOnly={Boolean(lastUser)}
                  />
                </FormField>
                <FormField name="password" label={t('login.fields.passwordLabel')} required>
                  <Input.Password
                    autoComplete="current-password"
                    placeholder={t('login.fields.passwordPlaceholder')}
                    size="large"
                  />
                </FormField>

                <div className="ds-login-form__row">
                  <FormField name="remember" valueProp="checked">
                    <Checkbox>{t('login.rememberMe')}</Checkbox>
                  </FormField>
                  <Link to={ROUTES.FORGOT_PASSWORD} className="ds-login-form__forgot">
                    {t('login.forgotPassword')}
                  </Link>
                </div>
              </AntForm>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loginMutation.isPending}
              >
                {t('login.submit')}
              </Button>
            </Form>
          )}

          {!isWide ? (
            <div className="ds-login-form__footer-mobile">
              {t('brand.footerMobile', {
                year: new Date().getFullYear(),
                version: import.meta.env.VITE_APP_VERSION ?? 'dev',
              })}
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
